/**
 * Batch Processes Shared State
 *
 * Contains always-on subscription data for active batch processes.
 * Powers the navbar dropdown indicator.
 */

import { BatchProcessesReadmodel } from '@bett3r-dev/teselly-domain';

export type BatchProcessesSharedState = {
  /** Active batch processes from subscription */
  activeProcesses: BatchProcessesReadmodel[];

  /**
   * Per-id converged progress (TV1-1979). Keyed by batch-process id, fanned out
   * over every id currently in `activeProcesses`. `projectedCount` is the count
   * of user-visible rows from the `batch-progress` readmodel; `loading` is true
   * between opening the per-id subscription and its first count roundtrip.
   */
  progressByProcessId: Record<string, { projectedCount: number; loading: boolean }>;

  /**
   * TV1-2446 slice 6 — the channel targets (`connectorId_integrationUserId`) of the active
   * processes whose credential is revoked. A process targeting one of them has Retry disabled
   * (the server would refuse it); see `firstRevokedChannelTarget`. Kept by
   * `revokedActiveChannelTargetsPolicy`; a failed read leaves an integration out (fail-open).
   */
  revokedChannelTargets: string[];

  /** Whether the navbar dropdown is open */
  dropdownOpen: boolean;
};

export const initialState: BatchProcessesSharedState = {
  activeProcesses: [],
  progressByProcessId: {},
  revokedChannelTargets: [],
  dropdownOpen: false
};

/**
 * Derived state selectors
 */
export const selectors = {
  /**
   * Overall indicator status for the navbar badge.
   * Priority: running > errored > idle.
   */
  indicatorStatus: ( state: BatchProcessesSharedState ): 'running' | 'errored' | 'idle' => {
    const hasRunning = state.activeProcesses.some( p => p.status === 'running' );
    if ( hasRunning ) return 'running';

    const hasErrored = state.activeProcesses.some( p => p.status === 'errored' );
    if ( hasErrored ) return 'errored';

    return 'idle';
  },

  /**
   * Active processes sorted for display: errored first, then running, by updatedAt desc.
   */
  sortedActiveProcesses: ( state: BatchProcessesSharedState ): BatchProcessesReadmodel[] => {
    const statusOrder: Record<string, number> = {
      errored: 0,
      running: 1,
      completed: 2,
      canceled: 3
    };

    return [...state.activeProcesses].sort(( a, b ) => {
      const orderA = statusOrder[a.status] ?? 99;
      const orderB = statusOrder[b.status] ?? 99;
      if ( orderA !== orderB ) return orderA - orderB;

      // Within same status, sort by updatedAt descending
      return new Date( b.updatedAt ).getTime() - new Date( a.updatedAt ).getTime();
    });
  },

  /**
   * Active processes sorted by updatedAt descending (newest first).
   * Used by the navbar dropdown — always shows most recent on top.
   */
  newestFirstProcesses: ( state: BatchProcessesSharedState ): BatchProcessesReadmodel[] => {
    return [...state.activeProcesses].sort(( a, b ) =>
      new Date( b.updatedAt ).getTime() - new Date( a.updatedAt ).getTime());
  },

  /**
   * Number of active processes.
   */
  activeCount: ( state: BatchProcessesSharedState ): number =>
    state.activeProcesses.length
};
